// Right-pane viewer + hand editor for a table artifact (#11727): a typed
// rows-and-columns grid rendered from the artifact's JSON content. Edit mode
// covers the schema (rename, retype, add, remove columns) and the data
// (edit cells, add, remove rows); Save PUTs the whole table through the
// artifact content endpoint, where the server's validator is the authority.
// Toolbar matches the shared artifact pane chrome.
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  X,
  Copy,
  Download,
  PencilLine,
  Check,
  ChevronDown,
  Plus,
  Trash2,
} from "lucide-react";
import { Tooltip } from "@mui/material";
import { saveTableSheet } from "./artifactDownload";
import { Menu, type MenuAction } from "../FpPages/RMS/design";
import { useViewerModeAction } from "../FpPages/ViewerModeToggle";
import { useArtifactPanelActions } from "./ArtifactPanelActions";
import ArtifactPaneHeader from "./ArtifactPaneHeader";
import ArtifactVersionHistoryPanel from "./ArtifactVersionHistoryPanel";
import { useArtifactHistory } from "./artifactHistory";
import RecordMentionChip from "../FpPages/Chat/components/RecordMentionChip";
import { isEntityKind } from "../FpPages/Chat/components/mentionTokens";

import {
  saveArtifactContent,
  type ArtifactVersion,
} from "../FpPages/Artifacts/useArtifacts";
import { useLocation } from "react-router-dom";
import { useFlash } from "@/hooks/productTimerHooks";
import { useFrameWidth } from "@/hooks/useFrameWidth";
import { useInvalidateArtifactVersions } from "./artifactVersions";
import {
  ArtifactSourcesButton,
  CitedCell,
  artifactCitationNumbers,
  artifactOriginLabel,
  citedCellSources,
  linkedCellSource,
  useArtifactCitations,
  useArtifactItem,
  type ArtifactSourceMap,
} from "./ArtifactSources";
import type { TableCellLink } from "./tableCellLink";
import { TableLinkCell } from "./TableLinkCell";
import {
  CellTextPopover,
  ChoiceMenu,
  ColumnPopover,
  SheetPopover,
  anchorFrom,
  type PopoverAnchor,
} from "./TableColumnPopover";
import {
  draftProblems,
  newColumnKey,
  newSheetName,
  cellSources,
  displayCell,
  parseTableContent,
  serializeTableContent,
  sheetToTsv,
  withCellSourcesCleared,
  isRecordCell,
  recordCellLabel,
  type TableArtifactContent,
  type TableCell,
  type TableColumn,
  type TableSheet,
} from "./tableArtifact";
import {
  citedCellId,
  citedCellsBySheet,
  firstCitedCell,
  type CitedCellIds,
} from "./citedTableCells";

interface TableArtifactPanelProps {
  /** Set when the table is an artifact — unlocks the header's action
   *  cluster. */
  artifactId?: string;
  /** False for a shared, not-mine artifact: cluster stays, pencil stands
   *  down (r60b). Defaults true. */
  editable?: boolean;
  title: string;
  /** The artifact's stored JSON content. */
  content: string;
  /** What a chat citation that opened the table cited (#12818): its figure,
   *  or the figures of its paragraph, or a name. The pane marks every cell
   *  holding one and lands on the first; a new array is a new open. */
  citedValues?: readonly string[];
  /** A deep link to one cell's source (#13788): the pane lands on the cell
   *  and opens the source beside the table, as the cell's Source entry
   *  does. A cell that no longer exists or no longer cites the marker
   *  opens the table alone. A new object is a new open. */
  cellLink?: TableCellLink;
  onClose: () => void;
}

const safeParse = (
  content: string
): { table: TableArtifactContent | null; error: string | null } => {
  try {
    return { table: parseTableContent(content), error: null };
  } catch (err: unknown) {
    return {
      table: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
};

const cloneSheet = (sheet: TableSheet): TableSheet => ({
  name: sheet.name,
  columns: sheet.columns.map((c) => ({ ...c })),
  rows: sheet.rows.map((r) => ({ ...r })),
  sources: sheet.rows.map((_r, i) => ({ ...(sheet.sources[i] ?? {}) })),
});

const cloneTable = (table: TableArtifactContent): TableArtifactContent => ({
  sheets: table.sheets.map(cloneSheet),
});

const replaceSheet = (
  table: TableArtifactContent,
  index: number,
  sheet: TableSheet
): TableArtifactContent => ({
  sheets: table.sheets.map((s, i) => (i === index ? sheet : s)),
});

// The table pane IS the RMS table (#11426 gaps-v2 grammar, the same CSS
// grid the record browser paints): a micro-caps header band on the 1.5%
// wash, one hairline under every row, no vertical rules, a hover wash (on
// the cell while reading, the row while editing), body in --fp-t-body /
// --fp-ink-2 with the first column in primary ink, an 8px resize band on each
// header edge whose boundary lights up in the accent, a slim add-column
// gutter, and blanks as an em dash in chrome ink. Every colour, size and
// distance reads the --fp-* tokens.
type ColumnWidths = Record<string, number>;
/** How long the pointer rests on a truncated cell before its full text shows. */
export const CELL_TOOLTIP_DELAY_MS = 400;

const MIN_COLUMN_WIDTH = 64;
const DEFAULT_COLUMN_WIDTH = 184;
const GUTTER_WIDTH = 36;

const widthsStorageKey = (artifactId: string | undefined): string | null =>
  artifactId ? `fp.tableArtifact.widths.${artifactId}` : null;

const readStoredWidths = (artifactId: string | undefined): ColumnWidths => {
  const key = widthsStorageKey(artifactId);
  if (!key) return {};
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") return {};
    const widths: ColumnWidths = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v)) widths[k] = v;
    }
    return widths;
  } catch {
    return {};
  }
};

// Column widths are a per-viewer convenience, not table data — kept in the
// browser per artifact so a width you set survives a reopen, never sent to
// the server.
const useColumnWidths = (
  artifactId: string | undefined
): [ColumnWidths, (key: string, width: number) => void] => {
  const [widths, setWidths] = useState<ColumnWidths>(() =>
    readStoredWidths(artifactId)
  );
  useEffect(() => {
    setWidths(readStoredWidths(artifactId));
  }, [artifactId]);
  const setWidth = (key: string, width: number) => {
    setWidths((current) => {
      const next = { ...current, [key]: Math.round(width) };
      const storageKey = widthsStorageKey(artifactId);
      if (storageKey) {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // Storage can be unavailable (private mode); the width still
          // applies for this pane's lifetime.
        }
      }
      return next;
    });
  };
  return [widths, setWidth];
};

interface GridSizing {
  widths: ColumnWidths;
  onResize: (key: string, width: number) => void;
}

// The same tracks in both modes (#11833 feedback): the RMS selection gutter
// on the left and the add-column gutter on the right are always laid out,
// empty while reading, so entering edit mode reflows nothing.
// The RMS grid's track rule (TableGrid.buildGridTracks, keeper ruling C3):
// text columns are minmax(width, 1fr) — at their set width when the table
// overflows, growing evenly when the card has slack, so the grid always
// meets the card's right edge and the add-column "+" sits at the last
// column's edge; number, date and boolean columns keep their exact width,
// since stretching a figure column only moves its value away from its
// neighbour. The add-column gutter is the RMS 42px cell.
const ADD_COLUMN_WIDTH = 42;
const columnWidth = (column: TableColumn, widths: ColumnWidths): number =>
  widths[column.key] ?? DEFAULT_COLUMN_WIDTH;
const gridTemplate = (columns: TableColumn[], widths: ColumnWidths): string =>
  [
    `${GUTTER_WIDTH}px`,
    ...columns.map((c) => {
      const width = columnWidth(c, widths);
      return c.type === "text" ? `minmax(${width}px, 1fr)` : `${width}px`;
    }),
    `${ADD_COLUMN_WIDTH}px`,
  ].join(" ");
const gridMinWidth = (columns: TableColumn[], widths: ColumnWidths): number =>
  GUTTER_WIDTH +
  columns.reduce((sum, c) => sum + columnWidth(c, widths), 0) +
  ADD_COLUMN_WIDTH;

/** The 8px band on a header's right edge (RMS ColumnHeader.ResizeHandle):
 *  the boundary line lights up in the accent on hover; dragging resizes the
 *  column from its current width. */
const ResizeHandle = ({
  label,
  onResize,
}: {
  label: string;
  onResize: (width: number) => void;
}) => (
  <div
    role="separator"
    aria-orientation="vertical"
    aria-label={`Resize column ${label}`}
    onPointerDown={(e) => {
      const cell = e.currentTarget.parentElement;
      if (!cell) return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = cell.getBoundingClientRect().width;
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);
      // A click on the band with a pixel of jitter must not write a width:
      // the drag has to travel a few pixels before it counts as a resize.
      let dragging = false;
      const move = (ev: PointerEvent) => {
        if (!dragging && Math.abs(ev.clientX - startX) < 3) return;
        dragging = true;
        onResize(
          Math.max(MIN_COLUMN_WIDTH, startWidth + (ev.clientX - startX))
        );
      };
      const up = () => {
        target.removeEventListener("pointermove", move);
        target.removeEventListener("pointerup", up);
      };
      target.addEventListener("pointermove", move);
      target.addEventListener("pointerup", up);
    }}
    className="group/resize absolute top-0 right-0 z-10 h-full w-2 cursor-col-resize"
  >
    <span
      aria-hidden
      className="pointer-events-none absolute top-0 right-0 h-full w-px bg-transparent transition-colors group-hover/resize:bg-[var(--fp-accent)]"
    />
  </div>
);

// The mock's .thead .th: micro-caps on the band wash, one hairline below.
// --fp-th-edge paints the column boundaries while the header row is hovered
// (index.css maps data-thead-hover to it), the same cue the RMS grid gives.
const headCellBase = "relative flex overflow-hidden text-left uppercase";
const headCellClass = `${headCellBase} items-center px-3`;
const headCellStyle: React.CSSProperties = {
  fontFamily: "var(--fp-font-ui)",
  fontSize: "var(--fp-t-micro)",
  fontWeight: "var(--fp-w-head)" as unknown as number,
  letterSpacing: "var(--fp-track-caps)",
  color: "var(--fp-ink-4)",
  background: "var(--fp-band)",
  boxShadow:
    "var(--fp-th-edge, inset 0 0 0 0 transparent), inset 0 -1px 0 0 var(--fp-border)",
};
const headTextStyle: React.CSSProperties = {
  fontFamily: "var(--fp-font-ui)",
  fontSize: "var(--fp-t-micro)",
  fontWeight: "var(--fp-w-head)" as unknown as number,
  letterSpacing: "var(--fp-track-caps)",
  color: "var(--fp-ink-4)",
};
const hairline = "inset 0 -1px 0 0 var(--fp-border)";
// Unpadded base for cells whose content brings its own padding (the edit
// inputs) or is centered (the gutters); Tailwind cannot subtract `px-3`
// back out with `p-0` — both are `p*` utilities and `px-3` sorts later.
const cellGround =
  "flex min-w-0 items-center overflow-hidden bg-[var(--fp-surface)]";
const bodyCellBase = `${cellGround} group-hover:bg-[var(--fp-hover-row)] transition-colors duration-150`;
const bodyCellClass = `${bodyCellBase} px-3`;
// The read view washes the one cell under the pointer rather than its row
// (Gerry, #12100): a table read cell by cell, as a spreadsheet is. Edit mode
// keeps the row wash above, since rows are what it selects.
const readCellClass = `${cellGround} px-3 hover:bg-[var(--fp-hover-row)] transition-colors duration-150`;
// The read view's empty gutters take no wash of their own.
const readGutterClass = `${cellGround} px-3`;
const bodyCellStyle: React.CSSProperties = {
  fontFamily: "var(--fp-font-ui)",
  fontSize: "var(--fp-t-body)",
  boxShadow: hairline,
};

const cellInk = (blank: boolean, first: boolean): React.CSSProperties =>
  blank
    ? { color: "var(--fp-ink-4)" }
    : first
      ? {
          color: "var(--fp-ink)",
          fontWeight: "var(--fp-w-primary)" as unknown as number,
        }
      : { color: "var(--fp-ink-2)" };

const gridStyle = (
  columns: TableColumn[],
  widths: ColumnWidths,
  rows: number
): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: gridTemplate(columns, widths),
  gridTemplateRows: `var(--fp-thead-h) repeat(${rows}, var(--fp-row-h))`,
  // As the RMS grid: fills the card when the columns come to less than its
  // width (the text tracks absorb the slack); the pixel min-width keeps the
  // horizontal scroll when they come to more.
  width: "100%",
  minWidth: `${gridMinWidth(columns, widths)}px`,
});

/** Header-row hover paints the column boundaries, exactly as the RMS grid
 *  does through the same data attribute index.css already styles. */
const useTheadHover = () => {
  const [hover, setHover] = useState(false);
  return {
    hover,
    onMouseOver: (e: React.MouseEvent<HTMLElement>) => {
      if ((e.target as HTMLElement).closest('[role="columnheader"]'))
        setHover(true);
    },
    onMouseOut: (e: React.MouseEvent<HTMLElement>) => {
      const to = e.relatedTarget as HTMLElement | null;
      if (!to || !to.closest?.('[role="columnheader"]')) setHover(false);
    },
  };
};

interface TableGridProps extends GridSizing {
  sheet: TableSheet;
  /** The artifact's citations (#11975): a cited cell wears the chat chip. */
  sourceMap?: ArtifactSourceMap | null;
  citationScope?: string | null;
  /** The cells a chat citation cited (#12818), marked as a cited fact is. */
  cited: CitedCellIds;
}

// A cited cell wears the mark a cited fact wears in the record preview
// (#11369): the accent ring and the accent wash, over the row hairline.
const citedCellStyle: React.CSSProperties = {
  background: "var(--fp-accent-soft)",
  boxShadow: `inset 0 0 0 2px var(--fp-accent), ${hairline}`,
};

/** The ids of the cells whose value their column truncates, measured in one
 *  pass over the grid on layout and again whenever the grid resizes (a
 *  column width, the pane). Measured up front rather than on
 *  hover, so the pointer cursor and the touch-visible control are right
 *  before any interaction. */
const useTruncatedCells = (
  gridRef: React.RefObject<HTMLDivElement | null>,
  sheet: TableSheet,
  widths: ColumnWidths,
  // A second source's chip widens a cited value once the map arrives.
  sourceMap: ArtifactSourceMap | null | undefined
): Set<string> => {
  const [truncated, setTruncated] = useState<Set<string>>(new Set());
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const measure = () => {
      const next = new Set<string>();
      grid.querySelectorAll<HTMLElement>("[data-cell-text]").forEach((text) => {
        if (text.scrollWidth > text.clientWidth + 1)
          next.add(text.dataset.cellText as string);
      });
      setTruncated((current) =>
        current.size === next.size && [...next].every((id) => current.has(id))
          ? current
          : next
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    // The UI face is a web font: a value measured in the fallback face can
    // widen when it swaps in without the grid itself resizing.
    let live = true;
    void document.fonts?.ready.then(() => {
      if (live) measure();
    });
    return () => {
      live = false;
      observer.disconnect();
    };
  }, [gridRef, sheet, widths, sourceMap]);
  return truncated;
};

const TableGrid = ({
  sheet,
  widths,
  onResize,
  sourceMap,
  citationScope,
  cited,
}: TableGridProps) => {
  const thead = useTheadHover();
  const rowCount = Math.max(sheet.rows.length, 1);
  const numbers = useMemo(
    () => artifactCitationNumbers(sourceMap),
    [sourceMap]
  );
  const gridRef = useRef<HTMLDivElement>(null);
  const truncated = useTruncatedCells(gridRef, sheet, widths, sourceMap);
  // The reader a truncated cell opens: its row, its column, and the cell.
  const [reading, setReading] = useState<{
    row: number;
    key: string;
    anchor: PopoverAnchor;
  } | null>(null);
  // The cell menu a right-click opens (the RMS grid's cell menu): the cell
  // it acts on and the pointer it opens at. The target outlives the open
  // flag, so the menu keeps its items while it animates closed.
  const [cellMenu, setCellMenu] = useState<{
    row: number;
    key: string;
    cell: HTMLElement;
    at: { top: number; left: number };
  } | null>(null);
  const [cellMenuOpen, setCellMenuOpen] = useState(false);
  // Row and key point into this sheet: a different sheet closes the reader
  // and the menu.
  useEffect(() => {
    setReading(null);
    setCellMenuOpen(false);
  }, [sheet]);
  // A source opening re-lays the table out or covers it (the drawer shows
  // the document beside it, or over it at phone, #12452), so the reader and
  // the menu make way: left open, they float over the document at a place
  // the table is no longer at.
  const transientOpen = reading !== null || cellMenuOpen;
  useEffect(() => {
    if (!transientOpen) return;
    const closeTransients = () => {
      setReading(null);
      setCellMenuOpen(false);
    };
    const names = ["openDocumentPanel", "openEntityPreview"];
    for (const name of names) window.addEventListener(name, closeTransients);
    return () => {
      for (const name of names) {
        window.removeEventListener(name, closeTransients);
      }
    };
  }, [transientOpen]);
  const toggleReading = (row: number, key: string, cell: HTMLElement) =>
    setReading((current) =>
      current?.row === row && current.key === key
        ? null
        : { row, key, anchor: anchorFrom(cell) }
    );
  // The value as the grid renders it: a cited value is a citation wherever
  // it shows, in the cell and in its reader.
  const cellContent = (rowIndex: number, column: TableColumn) => {
    const value = sheet.rows[rowIndex]?.[column.key] ?? null;
    // The citation is checked against the stored value; the reader sees it
    // in the column's format (a currency column's dollars).
    const text = displayCell(value, column.type);
    const shown = displayCell(value, column.type, column.format);
    // A record cell (#12430) is the record chip an @-mention shows in chat
    // (Gerry, 2026-10-02): it opens the record beside the table by the same
    // `openEntityPreview` event; the layout turns it into a navigation where
    // a preview would be wrong.
    if (isRecordCell(value) && isEntityKind(value.kind)) {
      return (
        <RecordMentionChip
          layout="inline"
          kind={value.kind}
          id={value.public_id}
          name={recordCellLabel(value)}
          truncate
        />
      );
    }
    if (column.type === "link" && typeof value === "string" && value) {
      return <TableLinkCell address={value} linkText={column.link_text} />;
    }
    const markers = cellSources(sheet, rowIndex, column.key);
    return value !== null && value !== "" && markers.length > 0 ? (
      <CitedCell
        text={text}
        shown={shown}
        columnType={column.type}
        markers={markers}
        sourceMap={sourceMap}
        numbers={numbers}
        citationScope={citationScope}
      />
    ) : (
      shown
    );
  };
  const readingColumn = reading
    ? sheet.columns.find((c) => c.key === reading.key)
    : undefined;
  // The RMS cell menu's actions that mean something on a table artifact, in
  // its order: Copy, then Source. No Show full text: the truncated cell's own
  // click, keyboard and hover tooltip already open its text (Gerry).
  const menuColumn = cellMenu
    ? sheet.columns.find((c) => c.key === cellMenu.key)
    : undefined;
  const cellMenuItems = (): MenuAction[] => {
    if (!cellMenu || !menuColumn) return [];
    const { row, key, cell } = cellMenu;
    const value = sheet.rows[row]?.[key] ?? null;
    const blank = value === null || value === "";
    // A blank cell copies an empty value, as the RMS grid's Copy does.
    // Copy takes the value as the cell shows it; the sources are found from
    // the stored value.
    const text = blank ? "" : displayCell(value, menuColumn.type);
    const shown = blank
      ? ""
      : displayCell(value, menuColumn.type, menuColumn.format);
    const items: MenuAction[] = [
      {
        label: "Copy",
        onSelect: () => {
          // Absent off a secure origin (a slot opened over plain http).
          navigator.clipboard?.writeText(shown).catch((err: unknown) => {
            console.error("Failed to copy:", err);
          });
        },
      },
    ];
    const sources = blank
      ? []
      : citedCellSources({
          text,
          shown,
          columnType: menuColumn.type,
          markers: cellSources(sheet, row, key),
          sourceMap,
          numbers,
          citationScope,
        });
    for (const source of sources) {
      items.push({
        label: sources.length === 1 ? "Source" : `Source: ${source.label}`,
        onSelect: source.open,
      });
    }
    return items;
  };
  return (
    <>
      <div
        ref={gridRef}
        role="grid"
        data-thead-hover={thead.hover ? "true" : undefined}
        onMouseOver={thead.onMouseOver}
        onMouseOut={thead.onMouseOut}
        style={gridStyle(sheet.columns, widths, rowCount)}
      >
        <div role="row" style={{ display: "contents" }}>
          {/* The selection gutter's track, empty while reading. */}
          <div role="columnheader" aria-hidden style={headCellStyle} />
          {sheet.columns.map((column) => (
            <div
              key={column.key}
              role="columnheader"
              className={headCellClass}
              style={headCellStyle}
            >
              <span className="min-w-0 truncate">{column.label}</span>
              <ResizeHandle
                label={column.label}
                onResize={(width) => onResize(column.key, width)}
              />
            </div>
          ))}
          <div role="columnheader" aria-hidden style={headCellStyle} />
        </div>
        {sheet.rows.length === 0 ? (
          <div role="row" style={{ display: "contents" }}>
            <div
              role="gridcell"
              className={`${readGutterClass} col-span-full justify-center`}
              style={{ ...bodyCellStyle, color: "var(--fp-ink-4)" }}
            >
              No rows yet
            </div>
          </div>
        ) : (
          sheet.rows.map((row, index) => (
            <div key={index} role="row" style={{ display: "contents" }}>
              <div
                role="gridcell"
                aria-hidden
                className={readGutterClass}
                style={bodyCellStyle}
              />
              {sheet.columns.map((column, columnIndex) => {
                const blank =
                  row[column.key] === null || row[column.key] === "";
                const id = citedCellId(index, column.key);
                const isTruncated = truncated.has(id);
                const isReading =
                  reading?.row === index && reading.key === column.key;
                const isCited = cited.has(id);
                return (
                  // The RMS grid's clipped-prose tooltip (FactCell): the whole
                  // value on hover, below the cell after 400ms. Every cell is
                  // wrapped, with an empty title while it fits or its reader
                  // is open, so crossing the truncation line never remounts
                  // the cell; describeChild keeps the cell's text its name.
                  <Tooltip
                    key={column.key}
                    title={
                      isTruncated && !isReading
                        ? displayCell(
                            row[column.key] ?? null,
                            column.type,
                            column.format
                          )
                        : ""
                    }
                    placement="bottom-start"
                    enterDelay={CELL_TOOLTIP_DELAY_MS}
                    enterNextDelay={CELL_TOOLTIP_DELAY_MS}
                    describeChild
                  >
                    <div
                      role="gridcell"
                      // The truncated cell is itself the way in, as an RMS
                      // long-text cell is (#10891): no icon, a click or Enter
                      // opens the reader.
                      data-cited={isCited ? "true" : undefined}
                      aria-haspopup={isTruncated ? "dialog" : undefined}
                      aria-expanded={isTruncated ? isReading : undefined}
                      tabIndex={isTruncated ? 0 : undefined}
                      className={`${readCellClass} ${
                        isTruncated
                          ? "cursor-pointer outline-none focus-visible:shadow-[inset_0_0_0_1px_var(--fp-accent)]"
                          : ""
                      }`}
                      style={{
                        ...bodyCellStyle,
                        ...cellInk(blank, columnIndex === 0),
                        ...(isCited ? citedCellStyle : {}),
                      }}
                      onClick={
                        isTruncated
                          ? (e) => {
                              // A cited value opens its source, a record
                              // chip its record, and a link its address,
                              // not the reader.
                              const target = e.target as HTMLElement;
                              if (
                                target.closest(
                                  ".fp-chat-citation, [data-record-mention], [data-table-link]"
                                )
                              )
                                return;
                              toggleReading(index, column.key, e.currentTarget);
                            }
                          : undefined
                      }
                      onKeyDown={
                        isTruncated
                          ? (e) => {
                              // Keys on the cited value inside are its own.
                              if (e.target !== e.currentTarget) return;
                              if (e.key !== "Enter" && e.key !== " ") return;
                              e.preventDefault();
                              toggleReading(index, column.key, e.currentTarget);
                            }
                          : undefined
                      }
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setCellMenu({
                          row: index,
                          key: column.key,
                          cell: e.currentTarget,
                          at: { top: e.clientY, left: e.clientX },
                        });
                        setCellMenuOpen(true);
                      }}
                    >
                      <span
                        data-cell-text={blank ? undefined : id}
                        className="min-w-0 truncate"
                      >
                        {cellContent(index, column)}
                      </span>
                    </div>
                  </Tooltip>
                );
              })}
              <div
                role="gridcell"
                aria-hidden
                className={readGutterClass}
                style={bodyCellStyle}
              />
            </div>
          ))
        )}
      </div>
      {reading && readingColumn && (
        <CellTextPopover
          key={citedCellId(reading.row, reading.key)}
          anchor={reading.anchor}
          label={readingColumn.label}
          onClose={() => {
            // Back to the cell it was read from, so the keyboard keeps its
            // place in the grid.
            reading.anchor.element.focus({ preventScroll: true });
            setReading(null);
          }}
        >
          {cellContent(reading.row, readingColumn)}
        </CellTextPopover>
      )}
      <Menu
        anchorEl={cellMenu?.cell ?? null}
        anchorPosition={cellMenu?.at ?? null}
        open={cellMenuOpen && menuColumn !== undefined}
        onClose={() => setCellMenuOpen(false)}
        items={cellMenuItems()}
        label={
          cellMenu && menuColumn
            ? `Row ${cellMenu.row + 1}, ${menuColumn.label}`
            : undefined
        }
      />
    </>
  );
};

interface TableEditorProps extends GridSizing {
  sheet: TableSheet;
  onChange: (next: TableSheet) => void;
}

// An editable cell is the same cell: a transparent, borderless input filling
// its slot; only the focused one takes the accent wash and inset ring.
const cellInputClass =
  "block h-full w-full min-w-0 truncate bg-transparent px-3 outline-none placeholder:text-[var(--fp-ink-4)] focus:bg-[var(--fp-accent-soft)] focus:shadow-[inset_0_0_0_1px_var(--fp-accent)]";
// Same ink rules as the view: first column primary, the rest secondary.
const cellInputStyle = (first: boolean): React.CSSProperties => ({
  fontFamily: "var(--fp-font-ui)",
  fontSize: "var(--fp-t-body)",
  ...cellInk(false, first),
});
/** The editable grid: same paint as the view. The header keeps its band
 *  and its one micro-caps line — each header is the trigger for its column
 *  popover (name, type, delete; #11833); body cells are inputs typed by
 *  their column, a boolean cell a menu; the trailing gutter holds the
 *  add-column control; a ghost row at the bottom adds a row. Column keys
 *  never change here — a rename is a relabel, so the data stays attached. */
const TableEditor = ({
  sheet,
  onChange,
  widths,
  onResize,
}: TableEditorProps) => {
  const thead = useTheadHover();
  // Row selection (the RMS gutter): tick rows, then delete them together
  // from the bar above the grid — no per-row trash icons.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const toggleRow = (index: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  const removeSelected = () => {
    const next = cloneSheet(sheet);
    // A removed row takes its sources with it (#11975): both lists are
    // aligned by index, so they are filtered together.
    next.sources = next.sources.filter((_, i) => !selected.has(i));
    next.rows = next.rows.filter((_, i) => !selected.has(i));
    setSelected(new Set());
    onChange(next);
  };
  const setColumn = (index: number, patch: Partial<TableColumn>) => {
    const next = cloneSheet(sheet);
    next.columns[index] = { ...next.columns[index], ...patch };
    onChange(next);
  };
  const removeColumn = (index: number) => {
    const next = cloneSheet(sheet);
    const [removed] = next.columns.splice(index, 1);
    next.rows = next.rows.map((row) => {
      const { [removed.key]: _dropped, ...rest } = row;
      return rest;
    });
    next.sources = next.sources.map((cell) => {
      const { [removed.key]: _dropped, ...rest } = cell;
      return rest;
    });
    onChange(next);
  };
  // The column popover (#11833): a header's name, type and delete live in
  // one popover opened from the header — click or right-click, as the RMS
  // column menu opens — so the header stays the RMS header while editing.
  const [openColumn, setOpenColumn] = useState<{
    index: number;
    anchor: PopoverAnchor;
    selectName: boolean;
  } | null>(null);
  const headerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const addColumnRef = useRef<HTMLDivElement | null>(null);
  // A just-added column's header does not exist until the next render, so
  // its popover opens from a layout effect keyed on its column key.
  const pendingColumn = useRef<string | null>(null);
  useLayoutEffect(() => {
    const key = pendingColumn.current;
    if (key === null) return;
    // One shot, whatever happens below: a key that never found its header
    // must not pop a popover on some later, unrelated column change.
    pendingColumn.current = null;
    const header = headerRefs.current[key];
    const index = sheet.columns.findIndex((c) => c.key === key);
    if (!header || index === -1) return;
    // The new column lands past the pane's right edge on a wide table;
    // bring the add-column gutter beside it into view first, so the user
    // sees both the column they just added and the "+" for the next one.
    (addColumnRef.current ?? header).scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
    setOpenColumn({ index, anchor: anchorFrom(header), selectName: true });
  }, [sheet.columns]);
  const addColumn = () => {
    const next = cloneSheet(sheet);
    const label = "New column";
    const key = newColumnKey(label, next.columns);
    next.columns.push({ key, label, type: "text" });
    next.rows = next.rows.map((row) => ({ ...row, [key]: null }));
    pendingColumn.current = key;
    onChange(next);
  };
  // The boolean cell's menu, anchored to the cell it edits.
  const [openCell, setOpenCell] = useState<{
    row: number;
    key: string;
    anchor: PopoverAnchor;
  } | null>(null);
  const setCell = (rowIndex: number, key: string, value: TableCell) => {
    // A hand-typed value no longer comes from the source (#11975): the
    // cell's citations go with the old value.
    const next = withCellSourcesCleared(cloneSheet(sheet), rowIndex, key);
    next.rows[rowIndex] = { ...next.rows[rowIndex], [key]: value };
    onChange(next);
  };
  const addRow = () => {
    const next = cloneSheet(sheet);
    const row: Record<string, TableCell> = {};
    for (const column of next.columns) row[column.key] = null;
    next.rows.push(row);
    next.sources.push({});
    onChange(next);
  };

  return (
    <>
      {selected.size > 0 && (
        <div
          className="sticky top-0 z-20 flex h-10 items-center gap-3 px-3"
          style={{
            fontFamily: "var(--fp-font-ui)",
            fontSize: "var(--fp-t-meta)",
            color: "var(--fp-accent)",
            background: "var(--fp-accent-soft)",
            boxShadow: hairline,
          }}
        >
          <span>
            {selected.size} {selected.size === 1 ? "row" : "rows"} selected
          </span>
          <button
            type="button"
            onClick={removeSelected}
            className="flex items-center gap-1 rounded-md px-2 py-1 font-medium hover:bg-[var(--fp-surface)]"
            style={{ color: "var(--fp-neg)" }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete {selected.size === 1 ? "row" : "rows"}
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="ml-auto rounded-md px-2 py-1 hover:bg-[var(--fp-surface)]"
          >
            Clear
          </button>
        </div>
      )}
      <div
        role="grid"
        data-thead-hover={thead.hover ? "true" : undefined}
        onMouseOver={thead.onMouseOver}
        onMouseOut={thead.onMouseOut}
        style={gridStyle(sheet.columns, widths, sheet.rows.length + 1)}
      >
        <div role="row" style={{ display: "contents" }}>
          {/* The gutter's header track is empty: a select-all circle beside
              the column names read as a delete for the header row (Gerry,
              2026-09-02). Rows are selected one by one. */}
          <div role="columnheader" aria-hidden style={headCellStyle} />
          {sheet.columns.map((column, index) => {
            const isOpen = openColumn?.index === index;
            const open = (element: HTMLButtonElement) =>
              setOpenColumn({
                index,
                anchor: anchorFrom(element),
                selectName: false,
              });
            return (
              <div
                key={column.key}
                role="columnheader"
                // The view header's paint exactly — editing must not restyle
                // the table; the whole cell is the popover's trigger, and it
                // takes the RMS sortable header's hover.
                className={`${headCellBase} group/col items-stretch`}
                style={headCellStyle}
                onContextMenu={(e) => {
                  e.preventDefault();
                  const header = headerRefs.current[column.key];
                  if (header) open(header);
                }}
              >
                <button
                  ref={(el) => {
                    headerRefs.current[column.key] = el;
                  }}
                  type="button"
                  aria-label={`Edit column ${column.label || "Column name"}`}
                  aria-haspopup="dialog"
                  aria-expanded={isOpen}
                  onClick={(e) =>
                    isOpen ? setOpenColumn(null) : open(e.currentTarget)
                  }
                  // `uppercase` restated on the button: preflight resets
                  // text-transform on buttons, so the cell's does not reach it.
                  className={`flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 px-3 text-left uppercase hover:bg-[var(--fp-band-hover)] ${
                    isOpen ? "bg-[var(--fp-band-hover)]" : ""
                  }`}
                  style={headTextStyle}
                >
                  {/* A blank label shows its placeholder in the band's ink
                      but as sentence case without the caps tracking: set in
                      micro-caps, "COLUMN NAME" reads as a column named that,
                      which is the one thing a placeholder must not do. */}
                  <span
                    className={`min-w-0 truncate ${
                      column.label ? "" : "normal-case"
                    }`}
                    style={
                      column.label ? undefined : { letterSpacing: "normal" }
                    }
                  >
                    {column.label || "Column name"}
                  </span>
                  <ChevronDown
                    aria-hidden
                    className={`h-3 w-3 flex-none transition-opacity ${
                      isOpen
                        ? "opacity-100"
                        : "opacity-0 group-hover/col:opacity-100"
                    }`}
                  />
                </button>
                <ResizeHandle
                  label={column.label}
                  onResize={(width) => onResize(column.key, width)}
                />
              </div>
            );
          })}
          <div
            ref={addColumnRef}
            role="columnheader"
            // The RMS add-column cell (ColumnSelector's header doorway, mock
            // .th-add): a slim gutter at the last column's edge holding a
            // 24px ghost icon-button that takes a visible edge only on hover.
            className={`${headCellBase} items-center justify-center`}
            style={headCellStyle}
          >
            <Tooltip title="Add column" arrow placement="bottom">
              <button
                type="button"
                aria-label="Add column"
                onClick={addColumn}
                className="flex h-6 w-6 items-center justify-center rounded-md border border-transparent text-[var(--fp-ink-4)] transition-colors hover:border-[var(--fp-border-strong)] hover:bg-[var(--fp-surface)] hover:text-[var(--fp-ink)]"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
        </div>
        {sheet.rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            role="row"
            className="group"
            style={{ display: "contents" }}
          >
            <div
              role="gridcell"
              className={`${bodyCellBase} justify-center`}
              style={{
                ...bodyCellStyle,
                ...selectedWash(selected.has(rowIndex)),
              }}
            >
              <SelectCircle
                label={`Select row ${rowIndex + 1}`}
                checked={selected.has(rowIndex)}
                onToggle={() => toggleRow(rowIndex)}
              />
            </div>
            {sheet.columns.map((column, columnIndex) => {
              const label = `Row ${rowIndex + 1}, ${column.label}`;
              const value = row[column.key] ?? null;
              return (
                <div
                  key={column.key}
                  role="gridcell"
                  className={bodyCellBase}
                  style={{
                    ...bodyCellStyle,
                    ...selectedWash(selected.has(rowIndex)),
                  }}
                >
                  {column.type === "boolean" ? (
                    <button
                      type="button"
                      aria-label={label}
                      aria-haspopup="menu"
                      aria-expanded={
                        openCell?.row === rowIndex &&
                        openCell.key === column.key
                      }
                      // Same gesture as the header: a click on the open
                      // cell closes its menu.
                      onClick={(e) =>
                        openCell?.row === rowIndex &&
                        openCell.key === column.key
                          ? setOpenCell(null)
                          : setOpenCell({
                              row: rowIndex,
                              key: column.key,
                              anchor: anchorFrom(e.currentTarget),
                            })
                      }
                      // Reads as a control the way the header does: pointer
                      // cursor, and the caret the row hover reveals.
                      className={`${cellInputClass} flex cursor-pointer items-center justify-between gap-1.5 text-left`}
                      style={{
                        ...cellInputStyle(columnIndex === 0),
                        ...(value === null ? { color: "var(--fp-ink-4)" } : {}),
                      }}
                    >
                      <span className="min-w-0 truncate">
                        {displayCell(value, "boolean")}
                      </span>
                      <ChevronDown
                        aria-hidden
                        className="h-3 w-3 flex-none text-[var(--fp-ink-4)] opacity-0 transition-opacity group-hover:opacity-100"
                      />
                    </button>
                  ) : column.type === "record" ? (
                    // A record is picked by resolving a name, not typed, so
                    // the editor shows it and leaves it alone (#12430).
                    <span
                      className={`${cellInputClass} flex items-center truncate`}
                      style={{
                        ...cellInputStyle(columnIndex === 0),
                        ...(value === null ? { color: "var(--fp-ink-4)" } : {}),
                      }}
                    >
                      {displayCell(value, "record")}
                    </span>
                  ) : (
                    <input
                      aria-label={label}
                      value={value === null ? "" : String(value)}
                      placeholder={
                        column.type === "date"
                          ? "YYYY-MM-DD"
                          : column.type === "link"
                            ? "https://"
                            : "—"
                      }
                      onChange={(e) =>
                        setCell(
                          rowIndex,
                          column.key,
                          e.target.value === "" ? null : e.target.value
                        )
                      }
                      // size=1: an input's intrinsic width is ~20 characters,
                      // which would inflate the 1fr tracks past the widths
                      // the read view sits at; the class already fills the
                      // cell.
                      size={1}
                      className={cellInputClass}
                      style={cellInputStyle(columnIndex === 0)}
                    />
                  )}
                </div>
              );
            })}
            <div
              role="gridcell"
              aria-hidden
              className={bodyCellClass}
              style={{
                ...bodyCellStyle,
                ...selectedWash(selected.has(rowIndex)),
              }}
            />
          </div>
        ))}
        <div role="row" style={{ display: "contents" }}>
          <button
            type="button"
            onClick={addRow}
            className="col-span-full flex items-center gap-1.5 px-3 text-left hover:bg-[var(--fp-hover-row)]"
            style={{
              fontFamily: "var(--fp-font-ui)",
              fontSize: "var(--fp-t-meta)",
              color: "var(--fp-ink-4)",
              boxShadow: hairline,
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add row
          </button>
        </div>
      </div>
      {openColumn && sheet.columns[openColumn.index] && (
        <ColumnPopover
          key={sheet.columns[openColumn.index].key}
          anchor={openColumn.anchor}
          column={sheet.columns[openColumn.index]}
          selectName={openColumn.selectName}
          onChange={(patch) => setColumn(openColumn.index, patch)}
          onDelete={() => {
            removeColumn(openColumn.index);
            setOpenColumn(null);
          }}
          onClose={() => setOpenColumn(null)}
        />
      )}
      {openCell && (
        <ChoiceMenu
          anchor={openCell.anchor}
          aria-label={`Row ${openCell.row + 1}, ${
            sheet.columns.find((c) => c.key === openCell.key)?.label ?? ""
          }`}
          choices={[
            { value: "", label: "—" },
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ]}
          value={
            sheet.rows[openCell.row]?.[openCell.key] == null
              ? ""
              : String(sheet.rows[openCell.row][openCell.key])
          }
          onPick={(picked) =>
            setCell(
              openCell.row,
              openCell.key,
              picked === "" ? null : picked === "true"
            )
          }
          onClose={() => {
            // Focus goes back to the cell the menu edited, so Tab and the
            // arrow keys carry on from where the user was.
            openCell.anchor.element.focus();
            setOpenCell(null);
          }}
        />
      )}
    </>
  );
};

/** The RMS gutter circle: faint at rest, filled in the accent when checked.
 *  Visible at rest deliberately (rules/frontend.md) — the record browser
 *  reveals it on table hover, but a hidden control is how "how do I delete a
 *  row" gets asked. */
const SelectCircle = ({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    aria-label={label}
    onClick={onToggle}
    className={`flex h-[17px] w-[17px] items-center justify-center rounded-full border-[1.5px] transition-colors ${
      checked
        ? "border-[var(--fp-accent)] bg-[var(--fp-accent)] text-[var(--fp-surface)]"
        : "border-[var(--fp-border-strong)] bg-transparent hover:border-[var(--fp-ink-3)]"
    }`}
  >
    {checked && <Check className="h-2.5 w-2.5" />}
  </button>
);

const NO_CITED_CELLS: CitedCellIds = new Set();

const selectedWash = (on: boolean): React.CSSProperties =>
  on ? { background: "var(--fp-accent-soft)" } : {};

const TableArtifactPanel: React.FC<TableArtifactPanelProps> = ({
  artifactId,
  editable = true,
  title,
  content,
  citedValues,
  cellLink,
  onClose,
}) => {
  // The pane owns the current table once mounted, so a saved hand-edit
  // renders without a reopen; different content arriving resets it.
  const parsed = useMemo(() => safeParse(content), [content]);
  const [table, setTable] = useState<TableArtifactContent | null>(parsed.table);
  const [draft, setDraft] = useState<TableArtifactContent | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(parsed.error);
  // The sheet the pane shows (#11727 slice 3): one tab active at a time,
  // Excel-style, clamped whenever the workbook changes shape.
  const [activeIndex, setActiveIndex] = useState(0);
  // A citation's open lands on its first cited cell (#12818): that cell's
  // sheet becomes the active one and the grid scrolls it into view. Decided
  // once per open (a new `citedValues`), in render so the first paint is
  // already on the right sheet. The landing belongs to the table it was
  // decided on: the pane stays mounted when a citation opens another table
  // into it, and the grid draws the old table for a render until the new
  // content is swapped in, so it lands only once that table is on screen.
  const [landedFor, setLandedFor] = useState<readonly string[] | undefined>(
    undefined
  );
  const [landing, setLanding] = useState<{
    id: string;
    table: TableArtifactContent;
  } | null>(null);
  if (citedValues !== landedFor) {
    setLandedFor(citedValues);
    const sheets = parsed.table?.sheets ?? [];
    const first = firstCitedCell(
      sheets,
      citedCellsBySheet(sheets, citedValues)
    );
    setLanding(
      first && parsed.table ? { id: first.id, table: parsed.table } : null
    );
    if (first) setActiveIndex(first.sheet);
  }
  // A deep link to a cell's source (#13788) lands the same way, decided once
  // per link against the table the pane opened with: the cell must still be
  // there, hold a value, and cite the link's marker, or the table opens
  // alone. The source opens once the artifact's map has been read.
  const [linkedFor, setLinkedFor] = useState<TableCellLink | undefined>(
    undefined
  );
  const [linked, setLinked] = useState<{
    sheet: number;
    row: number;
    key: string;
    marker: string;
    text: string;
    shown: string;
    columnType: TableColumn["type"];
  } | null>(null);
  // Every new open decides afresh, as the citation landing does: one with no
  // link, or a link that does not land, clears the last one, since the pane
  // stays mounted when another table opens into it.
  if (cellLink !== linkedFor) {
    setLinkedFor(cellLink);
    setLinked(null);
    if (cellLink) {
      const opened = parsed.table;
      const sheet = opened?.sheets[cellLink.sheet];
      const column = sheet?.columns.find((c) => c.key === cellLink.column);
      const value =
        sheet && cellLink.row < sheet.rows.length
          ? (sheet.rows[cellLink.row][cellLink.column] ?? null)
          : null;
      if (
        opened &&
        sheet &&
        column &&
        value !== null &&
        value !== "" &&
        cellSources(sheet, cellLink.row, cellLink.column).includes(
          cellLink.marker
        )
      ) {
        setLinked({
          sheet: cellLink.sheet,
          row: cellLink.row,
          key: column.key,
          marker: cellLink.marker,
          text: displayCell(value, column.type),
          shown: displayCell(value, column.type, column.format),
          columnType: column.type,
        });
        setActiveIndex(cellLink.sheet);
        setLanding({
          id: citedCellId(cellLink.row, column.key),
          table: opened,
        });
      }
    }
  }
  // When set, the pane shows this past version read-only.
  const [viewingVersion, setViewingVersion] = useState<{
    versionNumber: number;
    table: TableArtifactContent;
    sourceMap: ArtifactSourceMap | null;
  } | null>(null);
  // The current version's citations (#11975), re-read whenever the content
  // changes under the pane (a chat edit landing, a save).
  const citations = useArtifactCitations(artifactId, content);
  // The same read the citations come from: whether it has answered yet.
  const itemRead = useArtifactItem(artifactId);
  // Opened once per landed link, however often the effect re-runs.
  const openedLinkRef = useRef<typeof linked>(null);
  useEffect(() => {
    if (!linked || openedLinkRef.current === linked || itemRead.isPending)
      return;
    openedLinkRef.current = linked;
    if (!itemRead.isSuccess) return;
    linkedCellSource({
      text: linked.text,
      shown: linked.shown,
      columnType: linked.columnType,
      marker: linked.marker,
      sourceMap: citations.sourceMap,
      numbers: artifactCitationNumbers(citations.sourceMap),
      citationScope: citations.citationScope,
    })?.open();
  }, [
    linked,
    itemRead.isPending,
    itemRead.isSuccess,
    citations.sourceMap,
    citations.citationScope,
  ]);
  const shownSourceMap = viewingVersion
    ? viewingVersion.sourceMap
    : citations.sourceMap;

  // New content arriving (a chat edit landed, #11548 r44) swaps the live
  // table — but never wipes an in-progress hand edit: the user is editing
  // in the pane and chatting beside it, so the draft stays and a note says
  // the latest is one Cancel away.
  const [staleDraftNote, setStaleDraftNote] = useState(false);
  const invalidateVersions = useInvalidateArtifactVersions();
  // The content the pane mounted with: a later change is a chat edit
  // landing, and a new version (#12481), so the history column re-reads
  // the list; the mount itself is not.
  const mountedContentRef = useRef(parsed);
  useEffect(() => {
    setTable(parsed.table);
    setError(parsed.error);
    setViewingVersion(null);
    setDraft((current) => {
      if (current !== null) setStaleDraftNote(true);
      return current;
    });
    if (mountedContentRef.current !== parsed) {
      mountedContentRef.current = parsed;
      invalidateVersions(artifactId);
    }
  }, [parsed, artifactId, invalidateVersions]);

  const editing = draft !== null;
  const [widths, setWidth] = useColumnWidths(artifactId);
  // The grid scrolls sideways only when its columns' set widths are wider
  // than the card (#12481, Gerry: a scrollbar under a three-column empty
  // table). The grid fills the card exactly otherwise, and at a fractional
  // pane width the browser rounds that fill into a one-pixel overflow that
  // painted a full-width thumb.
  const { frameRef: scrollerRef, frameWidth: scrollerWidth } =
    useFrameWidth<HTMLDivElement>();
  // Whether the grid as drawn is wider than the card, read off the scroller
  // once the card, the sheet or a column's width has changed: the columns'
  // set widths underestimate what a phone draws (review: a 630px grid in a
  // 348px card, cut off with no way to pan to the rest), so the switch reads
  // the laid-out width, not the sum.
  const [gridOverflows, setGridOverflows] = useState(false);
  const shown = viewingVersion?.table ?? table;
  const book = editing && draft ? draft : shown;
  const sheetIndex = book
    ? Math.min(activeIndex, Math.max(book.sheets.length - 1, 0))
    : 0;
  const laidOutSheet = book?.sheets[sheetIndex] ?? null;
  const citedCells = useMemo(() => {
    const cells = shown ? citedCellsBySheet(shown.sheets, citedValues) : [];
    if (!linked) return cells;
    // The linked cell is marked like a cited one.
    const id = citedCellId(linked.row, linked.key);
    return cells.map((ids, index) =>
      index === linked.sheet ? new Set([...ids, id]) : ids
    );
  }, [shown, citedValues, linked]);
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const next = el.scrollWidth > el.clientWidth + 1;
    setGridOverflows((prev) => (prev === next ? prev : next));
  }, [scrollerRef, scrollerWidth, laidOutSheet, widths]);
  // The open a citation landed on scrolls its first cited cell into view,
  // once, when the read grid is drawing the table the landing was decided
  // on: reading on, the reader owns the scroll. Centred in the card's own
  // scroller, which alone moves: at its bottom edge the cell sat under the
  // page's floating buttons. Sideways it moves only as far as the value
  // needs to be in view, and not at all when it already is: a phone draws
  // a cost cell past the card's edge with its figure in view, and panning
  // to the cell's edge clipped the first column (#12818 review). Run here,
  // not in the grid, so the scroller's ref is attached by the time it runs.
  useLayoutEffect(() => {
    if (!landing || landing.table !== shown || editing) return;
    const scroller = scrollerRef.current;
    const value = scroller?.querySelector(
      `[role="grid"] [data-cell-text="${CSS.escape(landing.id)}"]`
    );
    const cell = value?.closest('[role="gridcell"]');
    if (scroller && value && cell) {
      const box = scroller.getBoundingClientRect();
      const row = cell.getBoundingClientRect();
      scroller.scrollTop += row.top - box.top - (box.height - row.height) / 2;
      const at = value.getBoundingClientRect();
      if (at.left < box.left) scroller.scrollLeft -= box.left - at.left;
      else if (at.right > box.right)
        scroller.scrollLeft += at.right - box.right;
    }
    setLanding(null);
  }, [landing, shown, editing, scrollerRef]);

  const download = () => {
    const source = viewingVersion?.table ?? table;
    if (!source) return;
    saveTableSheet(source, activeIndex, title);
  };

  const startEditing = () => {
    if (!table) return;
    setError(null);
    setStaleDraftNote(false);
    const clone = cloneTable(table);
    // An empty sheet edits like a spreadsheet, not a void (r69): open
    // with a screen of blank cells to type into. Trailing blank rows are
    // trimmed on save, so the scaffold never persists.
    clone.sheets = clone.sheets.map((sheet) =>
      sheet.rows.length === 0
        ? {
            ...sheet,
            rows: Array.from({ length: 8 }, () => ({})),
            sources: Array.from({ length: 8 }, () => ({})),
          }
        : sheet
    );
    setDraft(clone);
  };

  const rowIsBlank = (row: Record<string, unknown>): boolean =>
    Object.values(row).every(
      (v) => v === null || v === undefined || String(v).trim() === ""
    );

  const save = async () => {
    if (saving || !artifactId || !draft) return;
    // Spreadsheet semantics: trailing blank rows (the edit scaffold
    // included) never persist.
    const trimmed: TableArtifactContent = {
      sheets: draft.sheets.map((sheet) => {
        const rows = [...sheet.rows];
        const sources = rows.map((_row, i) => sheet.sources[i] ?? {});
        while (rows.length > 0 && rowIsBlank(rows[rows.length - 1])) {
          rows.pop();
          sources.pop();
        }
        return { ...sheet, rows, sources };
      }),
    };
    const problems = draftProblems(trimmed);
    if (problems.length > 0) {
      setError(problems.join(" "));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveArtifactContent(artifactId, {
        text: serializeTableContent(trimmed),
      });
      // Show what the server stored — the normalized table (typed numbers,
      // trimmed labels) rather than the raw draft.
      const stored = safeParse(saved.content ?? "");
      setTable(stored.table ?? trimmed);
      setDraft(null);
      setStaleDraftNote(false);
      invalidateVersions(artifactId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const addSheet = () => {
    if (!draft) return;
    const name = newSheetName(draft.sheets);
    setDraft({
      sheets: [
        ...draft.sheets,
        {
          name,
          columns: [{ key: "column_1", label: "Column 1", type: "text" }],
          rows: [],
          sources: [],
        },
      ],
    });
    setActiveIndex(draft.sheets.length);
  };
  const renameSheet = (index: number, name: string) => {
    if (!draft) return;
    setDraft(replaceSheet(draft, index, { ...draft.sheets[index], name }));
  };
  // Bumped when a sheet is removed: the sheet that slides into the removed
  // one's index must not inherit its editor state (row selection, open
  // popover) — with the index alone as the key it did (final review M1).
  const [editorEpoch, setEditorEpoch] = useState(0);
  const removeSheet = (index: number) => {
    if (!draft || draft.sheets.length <= 1) return;
    setDraft({ sheets: draft.sheets.filter((_, i) => i !== index) });
    setActiveIndex(Math.max(0, Math.min(index, draft.sheets.length - 2)));
    setEditorEpoch((epoch) => epoch + 1);
  };

  // Version history presentation (#11819): the other header actions stand
  // down while it is open, and it renders as a column beside the grid.
  const history = useArtifactHistory(artifactId);
  const { pathname } = useLocation();
  const [copied, flashCopied] = useFlash(2000);

  // The sheet on screen as tab-separated rows, header first (#12481): what
  // a spreadsheet or a chat composer pastes as a table.
  const copy = () => {
    if (!shown) return;
    const sheet = shown.sheets[Math.min(activeIndex, shown.sheets.length - 1)];
    void navigator.clipboard
      .writeText(sheetToTsv(sheet))
      .then(() => flashCopied());
  };
  const { actions: artifactActions, dialogs: artifactDialogs } =
    useArtifactPanelActions({
      artifactId: artifactId ?? null,
      title,
      file: {
        key: "download",
        label: "Download",
        icon: Download,
        onSelect: download,
      },
      copy: {
        key: "copy",
        label: "Copy",
        hint: copied ? "Copied!" : "Copy as tab-separated rows",
        icon: Copy,
        disabled: !shown,
        onSelect: copy,
      },
      edit:
        editable && !viewingVersion && !editing
          ? {
              key: "edit",
              label: "Edit table",
              icon: PencilLine,
              disabled: table === null,
              onSelect: startEditing,
            }
          : undefined,
    });
  const viewerModeAction = useViewerModeAction(true);

  // The history column's way of showing a version (#12481): a past
  // snapshot read-only, or the live table for null.
  const showVersion = (version: ArtifactVersion | null) => {
    if (version === null) {
      setViewingVersion(null);
      return;
    }
    if (version.content === null) return;
    const past = safeParse(version.content);
    if (!past.table) {
      setError(past.error);
      return;
    }
    setDraft(null);
    setViewingVersion({
      versionNumber: version.version_number,
      table: past.table,
      sourceMap: version.source_map ?? null,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* One panel chrome for every artifact kind (#11548 r51/r60): title
          on the left, its menu holding Copy, Edit table, the shared
          artifact actions, Version history and Download; Edit (in chat),
          the sources count, the layout toggle and Close on the right.
          The header folds as the pane narrows (#12091). */}
      <ArtifactPaneHeader
        title={title}
        onClose={onClose}
        badge={{ ext: "TBL", kind: "Table" }}
        origin={artifactOriginLabel(citations, pathname)}
        updatedAt={citations.updatedAt}
        version={
          artifactId
            ? {
                viewing: viewingVersion?.versionNumber ?? null,
                onView: showVersion,
                historyOpen: history.open,
                onToggleHistory: history.toggle,
              }
            : undefined
        }
        sources={
          !editing ? (
            <ArtifactSourcesButton
              sourceMap={shownSourceMap}
              citationScope={citations.citationScope}
            />
          ) : undefined
        }
        leading={
          editing ? (
            <>
              {/* Editing gets explicit, labeled actions — a bare toolbar
                  icon was too easy to miss as the way to save. */}
              <button
                type="button"
                onClick={() => {
                  setDraft(null);
                  setError(null);
                  setStaleDraftNote(false);
                }}
                className="rounded-[7px] border border-[var(--fp-border-strong)] px-3.5 py-2 text-[12.5px] font-medium text-[var(--fp-ink)] hover:bg-[var(--fp-hover)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="flex items-center gap-1.5 rounded-[7px] bg-[var(--fp-ink)] px-3.5 py-2 text-[12.5px] font-medium text-[var(--fp-surface)] hover:opacity-90 disabled:opacity-50"
              >
                <Check className="h-4 w-4" />
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : undefined
        }
        actions={[
          ...artifactActions,
          ...(!history.open && viewerModeAction ? [viewerModeAction] : []),
        ]}
      >
        {artifactDialogs}
      </ArtifactPaneHeader>
      {error && (
        <div className="mx-4 mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {editing && staleDraftNote && (
        <div className="mx-4 mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          This table was updated in chat while you were editing. Saving keeps
          your draft; Cancel shows the latest version.
        </div>
      )}
      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        {/* The RMS collection shell (#11833, Gerry): the table is a CARD —
            surface, hairline, --fp-card-r, corners clipping the header band.
            The pane itself is the floating card now (#12481, Gerry: the
            page ground inside it read as "weird grey"), so the body stays
            on the surface and the table's hairline alone frames it. The
            card is as tall as its rows and no taller (Gerry): it grows with
            content up to the pane, past which the rows scroll inside it.
            Sheets are the RMS view strip above the card, not a spreadsheet
            footer. The grid is the one thing that cannot wrap, so it
            scrolls INSIDE the card (the grid's horizontal hairline thumb)
            and never widens the pane. */}
        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          style={{
            padding: "var(--fp-gap) var(--fp-gap-wide) var(--fp-gap-wide)",
            gap: "var(--fp-gap-tight)",
          }}
        >
          {book && (book.sheets.length > 1 || editing) && (
            <SheetTabs
              sheets={book.sheets}
              active={sheetIndex}
              editing={editing}
              onSelect={setActiveIndex}
              onAdd={addSheet}
              onRename={renameSheet}
              onRemove={removeSheet}
            />
          )}
          <div
            data-table-card=""
            className="flex min-h-0 min-w-0 flex-col"
            style={{
              background: "var(--fp-surface)",
              // On the surface, the hairline alone frames the table: a lift
              // on white-on-white drew a grey halo (#12481).
              border: "1px solid var(--fp-border-strong)",
              borderRadius: "var(--fp-card-r)",
              overflow: "hidden",
            }}
          >
            <div
              ref={scrollerRef}
              // `overflow-x-auto` in the class list names this a deliberate
              // sideways scroller (the small-screen guard reads it there);
              // the inline rule takes the axis away while the grid fits.
              className="table-scroll-quiet relative min-h-0 min-w-0 overflow-auto overflow-x-auto"
              style={{
                overflowX:
                  scrollerWidth !== null && !gridOverflows ? "hidden" : "auto",
              }}
            >
              {editing && draft ? (
                <TableEditor
                  // Row selection is per sheet: remount on switch (review B1).
                  key={`${sheetIndex}:${editorEpoch}`}
                  sheet={draft.sheets[sheetIndex]}
                  onChange={(next) =>
                    setDraft(replaceSheet(draft, sheetIndex, next))
                  }
                  widths={widths}
                  onResize={setWidth}
                />
              ) : shown ? (
                <TableGrid
                  sheet={shown.sheets[sheetIndex]}
                  widths={widths}
                  onResize={setWidth}
                  sourceMap={shownSourceMap}
                  citationScope={citations.citationScope}
                  cited={citedCells[sheetIndex] ?? NO_CITED_CELLS}
                />
              ) : null}
            </div>
          </div>
          {/* No denominator: the RMS one speaks only for an incomplete set
              (register rule 22), and the pane always shows every row. */}
        </div>
        {history.open && artifactId && (
          <ArtifactVersionHistoryPanel
            artifactId={artifactId}
            viewing={viewingVersion?.versionNumber ?? null}
            onView={showVersion}
            onRestored={(version) => {
              if (version.content === null) return;
              const restored = safeParse(version.content);
              if (!restored.table) {
                setError(restored.error);
                return;
              }
              setDraft(null);
              setViewingVersion(null);
              setTable(restored.table);
            }}
            onError={setError}
          />
        )}
      </div>
    </div>
  );
};

// The RMS view strip's pin (RmsViewStrip.pinStyle): the on pin is a
// surface card with the strong hairline and the lift; the rest are quiet
// ink-3 text on the ground.
const sheetPinStyle = (on: boolean): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  flex: "none",
  cursor: "pointer",
  border: `1px solid ${on ? "var(--fp-border-strong)" : "transparent"}`,
  background: on ? "var(--fp-surface)" : "none",
  borderRadius: 8,
  padding: "6px 11px",
  fontFamily: "var(--fp-font-ui)",
  fontSize: "var(--fp-t-meta)",
  fontWeight: (on ? "var(--fp-w-strong)" : 500) as unknown as number,
  color: on ? "var(--fp-ink)" : "var(--fp-ink-3)",
  whiteSpace: "nowrap",
  maxWidth: 232,
  boxShadow: on ? "var(--fp-lift)" : "none",
  userSelect: "none",
});

/** The workbook's sheets as the RMS view strip (#11833) above the card:
 *  one pin per sheet, the active one a lifted surface pin. While editing a
 *  "+" adds a sheet and opens its popover, and a sheet is edited the way a
 *  column is: clicking the active pin, or right-clicking any pin, opens the
 *  sheet popover — name, and Delete sheet when there is more than one. */
const SheetTabs = ({
  sheets,
  active,
  editing,
  onSelect,
  onAdd,
  onRename,
  onRemove,
}: {
  sheets: TableSheet[];
  active: number;
  editing: boolean;
  onSelect: (index: number) => void;
  onAdd: () => void;
  onRename: (index: number, name: string) => void;
  onRemove: (index: number) => void;
}) => {
  const [open, setOpen] = useState<{
    index: number;
    anchor: PopoverAnchor;
  } | null>(null);
  const tabRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  // A popover that must wait for a render: "+" adds the sheet and opens its
  // popover once its tab exists (the add-column pattern); right-clicking a
  // non-active tab selects it first and anchors after the pins have taken
  // their new widths (final review L8).
  const pendingOpen = useRef<number | null>(null);
  useLayoutEffect(() => {
    const index = pendingOpen.current;
    if (index === null || index !== active) return;
    pendingOpen.current = null;
    const tab = tabRefs.current[index];
    if (!tab) return;
    tab.scrollIntoView({ block: "nearest", inline: "nearest" });
    setOpen({ index, anchor: anchorFrom(tab) });
  }, [sheets.length, active]);
  const openFor = (index: number) => {
    const tab = tabRefs.current[index];
    if (tab) setOpen({ index, anchor: anchorFrom(tab) });
  };
  return (
    <div
      role="tablist"
      aria-label="Sheets"
      className="fp-scroll-quiet flex flex-none items-center gap-1 overflow-x-auto"
    >
      {sheets.map((sheet, index) => {
        const isActive = index === active;
        const isOpen = open?.index === index;
        return (
          <button
            key={index}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={`Sheet ${sheet.name || "Sheet name"}`}
            aria-haspopup={editing ? "dialog" : undefined}
            aria-expanded={editing ? isOpen : undefined}
            onClick={() => {
              if (!isActive) {
                onSelect(index);
                return;
              }
              if (!editing) return;
              if (isOpen) setOpen(null);
              else openFor(index);
            }}
            onContextMenu={(e) => {
              if (!editing) return;
              e.preventDefault();
              if (isActive) {
                openFor(index);
                return;
              }
              pendingOpen.current = index;
              onSelect(index);
            }}
            className={editing ? "group/tab" : undefined}
            style={sheetPinStyle(isActive)}
          >
            <span className={sheet.name ? "" : "text-[var(--fp-ink-4)]"}>
              {sheet.name || "Sheet name"}
            </span>
            {/* The active tab is also the sheet's editor while editing:
                the header's caret, shown on hover, says so. */}
            {editing && isActive && (
              <ChevronDown
                aria-hidden
                className={`h-3 w-3 flex-none text-[var(--fp-ink-4)] transition-opacity ${
                  isOpen
                    ? "opacity-100"
                    : "opacity-0 group-hover/tab:opacity-100"
                }`}
              />
            )}
          </button>
        );
      })}
      {editing && (
        <button
          type="button"
          aria-label="Add sheet"
          onClick={() => {
            pendingOpen.current = sheets.length;
            onAdd();
          }}
          className="hover:!text-[var(--fp-ink)]"
          style={{ ...sheetPinStyle(false), padding: "6px 8px" }}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      )}
      {open && sheets[open.index] && (
        <SheetPopover
          key={open.index}
          anchor={open.anchor}
          name={sheets[open.index].name}
          removable={sheets.length > 1}
          onRename={(name) => onRename(open.index, name)}
          onRemove={() => {
            onRemove(open.index);
            setOpen(null);
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
};

export default TableArtifactPanel;
